import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';

const ScaleUpCalculator = ({ onCalculate }) => {
  const [formData, setFormData] = useState({
    targetProduction: '',
    currentProduction: '',
    growthPercentage: '',
    timeframe: '12'
  });

  const [results, setResults] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const calculateRequirements = () => {
    setIsCalculating(true);
    
    setTimeout(() => {
      const target = parseFloat(formData?.targetProduction) || 0;
      const current = parseFloat(formData?.currentProduction) || 0;
      const growth = parseFloat(formData?.growthPercentage) || 0;
      
      const productionGap = target - current;
      const percentageIncrease = ((target - current) / current) * 100;
      
      const equipmentNeeds = [
        {
          id: 1,
          equipment: 'Mesin Pasteurisasi Tambahan',
          priority: 'Tinggi',
          quantity: Math.ceil(productionGap / 500),
          estimatedCost: Math.ceil(productionGap / 500) * 75000000,
          reason: 'Kapasitas pasteurisasi saat ini tidak mencukupi untuk target produksi',
          icon: 'Flame'
        },
        {
          id: 2,
          equipment: 'Tangki Penyimpanan Dingin',
          priority: 'Tinggi',
          quantity: Math.ceil(productionGap / 1000),
          estimatedCost: Math.ceil(productionGap / 1000) * 45000000,
          reason: 'Diperlukan untuk menjaga kualitas susu dengan volume produksi yang lebih tinggi',
          icon: 'Snowflake'
        },
        {
          id: 3,
          equipment: 'Mesin Pengemas Otomatis',
          priority: 'Sedang',
          quantity: Math.ceil(productionGap / 800),
          estimatedCost: Math.ceil(productionGap / 800) * 120000000,
          reason: 'Meningkatkan efisiensi pengemasan dan mengurangi biaya tenaga kerja',
          icon: 'Package'
        },
        {
          id: 4,
          equipment: 'Sistem Filtrasi Lanjutan',
          priority: 'Sedang',
          quantity: 1,
          estimatedCost: 85000000,
          reason: 'Memastikan kualitas produk tetap konsisten dengan volume yang lebih besar',
          icon: 'Filter'
        }
      ];

      const totalInvestment = equipmentNeeds?.reduce((sum, item) => sum + item?.estimatedCost, 0);

      setResults({
        productionGap,
        percentageIncrease,
        equipmentNeeds,
        totalInvestment,
        timeframe: formData?.timeframe
      });

      setIsCalculating(false);
      
      if (onCalculate) {
        onCalculate({
          productionGap,
          percentageIncrease,
          totalInvestment
        });
      }
    }, 1500);
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'Tinggi': 'text-error bg-error/10',
      'Sedang': 'text-warning bg-warning/10',
      'Rendah': 'text-success bg-success/10'
    };
    return colors?.[priority] || 'text-muted-foreground bg-muted';
  };

  return (
    <div className="space-y-6 md:space-y-8">
      {/* Input Form */}
      <div className="bg-card rounded-xl border border-border p-6 md:p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
            <Icon name="Calculator" size={20} color="var(--color-accent)" />
          </div>
          <h3 className="text-lg md:text-xl font-semibold text-foreground">Kalkulator Kebutuhan Scale-Up</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          <Input
            label="Produksi Saat Ini (Liter/Hari)"
            type="number"
            name="currentProduction"
            value={formData?.currentProduction}
            onChange={handleInputChange}
            placeholder="Contoh: 1000"
            required
          />

          <Input
            label="Target Produksi (Liter/Hari)"
            type="number"
            name="targetProduction"
            value={formData?.targetProduction}
            onChange={handleInputChange}
            placeholder="Contoh: 2500"
            required
          />

          <Input
            label="Persentase Pertumbuhan (%)"
            type="number"
            name="growthPercentage"
            value={formData?.growthPercentage}
            onChange={handleInputChange}
            placeholder="Contoh: 150"
            description="Persentase peningkatan dari produksi saat ini"
          />

          <Input
            label="Jangka Waktu (Bulan)"
            type="number"
            name="timeframe"
            value={formData?.timeframe}
            onChange={handleInputChange}
            placeholder="Contoh: 12"
          />
        </div>

        <div className="mt-6">
          <Button
            variant="default"
            size="lg"
            fullWidth
            iconName="Calculator"
            iconPosition="left"
            loading={isCalculating}
            onClick={calculateRequirements}
            disabled={!formData?.currentProduction || !formData?.targetProduction}
          >
            {isCalculating ? 'Menghitung...' : 'Hitung Kebutuhan Peralatan'}
          </Button>
        </div>
      </div>
      {/* Results Display */}
      {results && (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-card rounded-xl border border-border p-6">
              <div className="flex items-center gap-3 mb-2">
                <Icon name="TrendingUp" size={20} color="var(--color-primary)" />
                <p className="text-sm text-muted-foreground">Kesenjangan Produksi</p>
              </div>
              <p className="text-2xl md:text-3xl font-bold text-foreground data-text">
                {results?.productionGap?.toLocaleString('id-ID')}
              </p>
              <p className="text-xs text-muted-foreground mt-1">Liter/Hari</p>
            </div>

            <div className="bg-card rounded-xl border border-border p-6">
              <div className="flex items-center gap-3 mb-2">
                <Icon name="Percent" size={20} color="var(--color-accent)" />
                <p className="text-sm text-muted-foreground">Peningkatan</p>
              </div>
              <p className="text-2xl md:text-3xl font-bold text-foreground data-text">
                {results?.percentageIncrease?.toFixed(1)}%
              </p>
              <p className="text-xs text-muted-foreground mt-1">Dari Kapasitas Saat Ini</p>
            </div>

            <div className="bg-card rounded-xl border border-border p-6">
              <div className="flex items-center gap-3 mb-2">
                <Icon name="DollarSign" size={20} color="var(--color-success)" />
                <p className="text-sm text-muted-foreground">Total Investasi</p>
              </div>
              <p className="text-xl md:text-2xl font-bold text-foreground data-text">
                Rp {(results?.totalInvestment / 1000000)?.toFixed(1)}M
              </p>
              <p className="text-xs text-muted-foreground mt-1">Estimasi Biaya Peralatan</p>
            </div>
          </div>

          {/* Equipment Recommendations */}
          <div className="bg-card rounded-xl border border-border p-6 md:p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Icon name="ListChecks" size={20} color="var(--color-primary)" />
              </div>
              <h3 className="text-lg md:text-xl font-semibold text-foreground">Rekomendasi Peralatan</h3>
            </div>

            <div className="space-y-4">
              {results?.equipmentNeeds?.map((item, index) => (
                <div key={item?.id} className="bg-muted/30 rounded-xl p-4 md:p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Icon name={item?.icon} size={24} color="var(--color-primary)" />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4 mb-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-sm font-medium text-muted-foreground">#{index + 1}</span>
                            <h4 className="text-base md:text-lg font-semibold text-foreground">{item?.equipment}</h4>
                          </div>
                          <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-medium ${getPriorityColor(item?.priority)}`}>
                            <Icon name="Flag" size={12} />
                            Prioritas {item?.priority}
                          </span>
                        </div>
                        
                        <div className="text-right">
                          <p className="text-lg md:text-xl font-bold text-foreground data-text">
                            Rp {(item?.estimatedCost / 1000000)?.toFixed(1)}M
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">Estimasi Biaya</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                        <div className="flex items-center gap-2">
                          <Icon name="Package" size={16} color="var(--color-muted-foreground)" />
                          <span className="text-sm text-muted-foreground">Jumlah:</span>
                          <span className="text-sm font-medium text-foreground data-text">{item?.quantity} Unit</span>
                        </div>
                      </div>

                      <div className="bg-card rounded-lg p-3 border border-border">
                        <p className="text-xs text-muted-foreground mb-1">Alasan:</p>
                        <p className="text-sm text-foreground">{item?.reason}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScaleUpCalculator;